import { AfterViewInit, Component, inject, effect, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Toast } from 'primeng/toast';
import { Button } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { AppUpdateService } from './core/services/app-update.service';
import { MigrationProgressToastService } from './core/services/migration-progress-toast.service';
import { NotificationService } from './core/services/notification.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InactivityService } from './core/services/inactivity.service';
import { AppStore } from './core/states';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastMessageComponent } from './shared/components/toast-message/toast-message.component';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';
import {
  applyRootThemeClass,
  getDarkModeMediaQuery,
  resolveIsDarkTheme,
  subscribeToDarkModeChanges,
  updateThemeColorMeta,
} from './core/theme/theme.runtime';

@Component({
  standalone: true,
  imports: [
    RouterModule,
    Toast,
    Button,
    ProgressBarModule,
    ConfirmDialogModule,
    TranslateModule,
    ToastMessageComponent,
  ],
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>
    <p-confirmDialog
      styleClass="m-4"
      [header]="'common.notifications.information_header' | translate"
      [attr.aria-label]="'common.notifications.information_header' | translate"
    />
    <p-toast position="top-center" key="tc">
      <ng-template #message let-message>
        <app-toast-message [message]="message" />
      </ng-template>
    </p-toast>
    <p-toast position="bottom-center" key="bc">
      <ng-template #message let-message>
        <app-toast-message [message]="message" />
      </ng-template>
    </p-toast>
    <p-toast position="center" key="center">
      <ng-template #message let-message>
        <app-toast-message [message]="message" />
      </ng-template>
    </p-toast>
    <p-toast position="top-center" key="migration-progress">
      <ng-template #headless let-message>
        @if (migrationProgressToast.progressData(); as progress) {
          <div class="flex flex-col p-4 gap-4">
            <div class="flex items-center gap-4">
              <i class="pi pi-database text-white text-2xl"></i>
              <span class="font-bold text-base text-white">{{ message.summary }}</span>
            </div>
            <div class="flex flex-col gap-2">
              <div class="text-sm text-white wrap-break-word">
                {{ 'common.notifications.migration_progress_message' | translate: { name: progress.currentName } }}
              </div>
              <p-progressBar [value]="progress.progressPercent" [showValue]="false" [style]="{ height: '4px' }" />
              <div class="flex items-center justify-between text-xs text-white/90">
                <span>
                  {{
                    'common.notifications.migration_progress_counter'
                      | translate: { current: progress.currentIndex, total: progress.total }
                  }}
                </span>
                <span>#{{ progress.currentVersion }}</span>
              </div>
            </div>
          </div>
        }
      </ng-template>
    </p-toast>
    <p-toast position="top-center" key="app-update">
      <ng-template #message let-message>
        <div class="flex flex-col gap-3 w-full">
          <div class="flex items-center gap-3">
            <i class="pi pi-info-circle text-2xl"></i>
            <div class="flex-1">
              <span class="font-semibold block">{{ message.summary }}</span>
              <div class="text-sm mt-1">{{ message.detail }}</div>
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <p-button
              styleClass="min-w-25"
              [label]="'common.actions.later' | translate"
              severity="secondary"
              size="small"
              [outlined]="true"
              (onClick)="onUpdateLater()"
            />
            <p-button
              styleClass="min-w-25"
              [label]="'common.actions.update' | translate"
              severity="info"
              [outlined]="true"
              size="small"
              (onClick)="onUpdateClick()"
            />
          </div>
        </div>
      </ng-template>
    </p-toast>
    <p-toast position="top-center" key="app-update-downloading">
      <ng-template #message let-message>
        <div class="flex items-center gap-3 w-full">
          <i class="pi pi-spin pi-spinner text-2xl"></i>
          <div class="flex-1">
            <span class="font-semibold block">{{ message.summary }}</span>
            <div class="text-sm mt-1">{{ message.detail }}</div>
          </div>
        </div>
      </ng-template>
    </p-toast>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
    }
  `,
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly notificationService = inject(NotificationService);
  private readonly inactivityService = inject(InactivityService);
  private readonly appStore = inject(AppStore);
  private readonly translateService = inject(TranslateService);
  private readonly darkModeMediaQuery = getDarkModeMediaQuery();
  readonly migrationProgressToast = inject(MigrationProgressToastService);

  constructor() {
    // Listen for available updates and show toast
    this.appUpdateService.updateAvailable$.pipe(takeUntilDestroyed()).subscribe(({ currentHash, newHash, appData }) => {
      console.log('[UI] Showing update prompt:', currentHash, '->', newHash);
      this.notificationService.clear('app-update');
      const detail = this.translateService.instant('common.notifications.update_available_message', {
        version: appData.version,
      });
      const summary = this.translateService.instant('common.notifications.update_available_header');
      this.notificationService.info(detail, summary, {
        sticky: true,
        life: 0,
        closable: false,
        key: 'app-update',
      });
    });

    // todo [ds]: it is not a good place to activate background service
    // Setup inactivity timeout - start watching when user is authorized, stop when logged out
    effect(() => {
      const isAuthorized = this.appStore.isAuthorized();

      if (isAuthorized) {
        this.inactivityService.start();
      } else {
        this.inactivityService.stop();
      }
    });

    effect((onCleanup) => {
      const selectedTheme = this.appStore.uiTheme();
      const applyTheme = () => {
        const prefersDark = this.darkModeMediaQuery?.matches ?? true;
        const isDark = resolveIsDarkTheme(selectedTheme, prefersDark);
        applyRootThemeClass(isDark);
        updateThemeColorMeta(isDark);
      };

      applyTheme();

      if (selectedTheme === 'system' && this.darkModeMediaQuery) {
        const unsubscribe = subscribeToDarkModeChanges(this.darkModeMediaQuery, applyTheme);
        onCleanup(() => unsubscribe());
      }
    });
  }

  ngOnInit() {
    void 0;
  }

  async ngAfterViewInit() {
    if (Capacitor.isNativePlatform()) {
      await SplashScreen.hide({
        // fadeOutDuration: 500, // Adds a smooth half-second fade
      });
    }
    // Notify toast service that <p-toast> host is mounted and deferred show can be flushed.
    this.migrationProgressToast.markToastHostReady();
  }

  onUpdateClick(): void {
    // Close the update toast before starting the download so the UI can re-render
    this.notificationService.clear('app-update');

    const downloadingMessage = this.translateService.instant('common.notifications.update_downloading_message');
    const header = this.translateService.instant('common.notifications.update_available_header');
    this.notificationService.info(downloadingMessage, header, {
      sticky: true,
      closable: false,
      key: 'app-update-downloading',
    });

    // Defer the actual update to give Angular one rendering frame to dismiss the toast
    setTimeout(() => {
      void this.appUpdateService
        .activateUpdate()
        .catch((error: unknown) => {
          console.error('[Update] Failed to activate update:', error);
          this.notificationService.error(
            this.translateService.instant('common.notifications.update_error_message'),
            this.translateService.instant('common.notifications.update_available_header'),
          );
        })
        .finally(() => {
          this.notificationService.clear('app-update-downloading');
        });
    }, 50);
  }

  onUpdateLater(): void {
    this.notificationService.clear('app-update');
  }
}
